import { styled } from "@mui/material/styles";
import DojoView from "csi/page/DojoView";
import { getReactComponentInfo } from "csi/service/layoutService";
import { useStore } from "csi/state/hooks";
import { LayoutItem } from "csi/state/layout";
import { CacheContext } from "designSystem/hooks";
import { displayNone } from "designSystem/styles/css";
import { observer } from "mobx-react-lite";
import React, { Fragment, FunctionComponent, useEffect } from "react";

const StyledDojoView = styled(DojoView)(({
	theme: { spacing },
}) => ({
	paddingLeft: spacing(4),
	paddingRight: spacing(4),
	paddingTop: spacing(3),
}));

interface LayoutItemViewProps {
	/** item to render */
	item: LayoutItem;

	/** selected status of item */
	selected: boolean;
}

const LayoutItemView: FunctionComponent<LayoutItemViewProps> = observer((props) => {
	const { layoutStore, routerStore } = useStore();
	const { useFlexHeight, viewParams } = layoutStore;
	const { refreshTimestamp } = routerStore;
	const { item, selected } = props;
	const { id, node, options, parent, view, viewOwnerType, visible } = item;


	useEffect(() => {
		// if the view is selected and it already exists, then notify the view of the switch and resize
		if (selected && visible && view?.isValidView() && view?.bViewed) {
			view.resize();
		}
	}, [selected, visible]);

	// NOTE: only pass viewNodeType if the view is a switchboard and the owner is a different node than the root node

	// render react component or dojo view based on the type
	const { ReactComponent, reactViewProps, reactView, isReact } = getReactComponentInfo(node, id);

	return (
		<Fragment>
			{isReact && selected &&
				<ReactComponent {...reactView.properties} {...reactViewProps} />
			}
			{!isReact && (
				<StyledDojoView
					className={selected ? "" : displayNone}
					fullHeight={useFlexHeight}
					nodeUrl={node.$URL}
					refreshTimestamp={refreshTimestamp}
					switchboard={options?.switchboard}
					switchboardOwnerType={parent.viewOwnerType}
					viewId={id}
					viewNodeType={options?.switchboard ? viewOwnerType : null}
					viewParams={{
						layoutItem: item,
						...viewParams,
					}}
				/>
			)}
		</Fragment>
	);
});

const LayoutContent = observer(() => {
	const { layoutStore } = useStore();
	const { cache, selectedItem, visibleViewedItems } = layoutStore;

	return (
		<CacheContext.Provider value={cache}>
			{
				visibleViewedItems.map((item) => (
					<LayoutItemView
						item={item}
						key={item.uniqueId}
						selected={item === selectedItem}
					/>
				),
				)
			}
		</CacheContext.Provider>
	);
});

export default LayoutContent;